import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"

import type { UserReport } from "../api/user-reports-service"
import type { ReportId } from "../config/reports-data"

/** Default cosmetic "your reading unlocks in" window after a report purchase. */
export const UNLOCK_DURATION_MS = 10 * 60 * 1000

/** Included Soulmate / Astro Map window; after liked review → 0 if ready, else 10‑min. */
const INCLUDED_REPORT_UNLOCK_DURATION_MS = 30 * 60 * 1000

/** Keep in sync with ReportIds.Soulmate / ReportIds.AstroMap / ReportIds.AuraScan. */
export const INCLUDED_UNLOCK_REPORT_IDS = [
  "soulmate-sketch",
  "astro-map",
  "aura-scan",
] as const satisfies readonly ReportId[]

const SHORTENED_STORAGE_KEY = "included_report_unlock_shortened"
const INSTANT_STORAGE_KEY = "included_report_unlock_instant"

/**
 * Post-purchase "report in progress" timers, keyed by report id.
 *
 * We store the purchase START timestamp (not a ticking countdown), so the time
 * left is always derived from the clock — it stays correct across navigation,
 * reloads and backgrounded tabs. Persisted to sessionStorage so it survives
 * leaving the screen and refreshes within the session. It is deliberately NOT a
 * source of truth: on logout we clear it (see the logout modal), after which the
 * server-side "purchased" entitlement shows the report as available anyway. So a
 * lost timer degrades gracefully to "report ready", never to a broken state.
 *
 * Included-plan Soulmate / Astro Map unlocks are an exception: their `startedAt`
 * is synced from `subscription.startedAt` on webapp entry (see
 * {@link syncIncludedReportUnlock}). Liked-review outcomes (instant unlock or
 * 10‑min shorten) are persisted in localStorage so re-sync keeps them.
 */
interface ReportUnlockState {
  /** reportId -> purchase / subscription start (epoch ms). */
  startedAt: Partial<Record<ReportId, number>>
  /** reportId -> unlock window length; missing means {@link UNLOCK_DURATION_MS}. */
  durationMs: Partial<Record<ReportId, number>>
  /** Begin the unlock window for a report (no-op if one is already running). */
  startUnlock: (reportId: ReportId, durationMs?: number) => void
  /**
   * Force-set unlock from an authoritative start time (subscription.startedAt).
   * Always overwrites startedAt; duration comes from liked-review flags or the
   * provided default (30‑min for included plans).
   */
  syncUnlock: (reportId: ReportId, startedAtMs: number, durationMs: number) => void
  /** Drop one report's timer (liked-review instant unlock). */
  clearUnlock: (reportId: ReportId) => void
  /** Drop all timers (used on logout). */
  clearAll: () => void
}

function readIdSet(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? new Set(parsed.filter((x) => typeof x === "string")) : new Set()
  } catch {
    return new Set()
  }
}

function writeIdSet(key: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]))
  } catch {
    /* ignore */
  }
}

function clearIdSet(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function markIncludedUnlockShortened(reportId: ReportId): void {
  const ids = readIdSet(SHORTENED_STORAGE_KEY)
  ids.add(reportId)
  writeIdSet(SHORTENED_STORAGE_KEY, ids)
}

function markIncludedUnlockInstant(reportId: ReportId): void {
  const ids = readIdSet(INSTANT_STORAGE_KEY)
  ids.add(reportId)
  writeIdSet(INSTANT_STORAGE_KEY, ids)
}

/** True after liked-review instant unlock — no included timer should ever show again. */
export function isIncludedUnlockInstant(reportId: ReportId): boolean {
  return readIdSet(INSTANT_STORAGE_KEY).has(reportId)
}

/** Duration for an included-plan report: 10‑min after liked review, else 30‑min. */
function includedUnlockDurationMs(reportId: ReportId): number {
  return readIdSet(SHORTENED_STORAGE_KEY).has(reportId)
    ? UNLOCK_DURATION_MS
    : INCLUDED_REPORT_UNLOCK_DURATION_MS
}

export const useReportUnlockStore = create<ReportUnlockState>()(
  persist(
    (set) => ({
      startedAt: {},
      durationMs: {},
      startUnlock: (reportId, durationMs = UNLOCK_DURATION_MS) =>
        set((state) =>
          state.startedAt[reportId]
            ? state
            : {
                startedAt: { ...state.startedAt, [reportId]: Date.now() },
                durationMs: { ...state.durationMs, [reportId]: durationMs },
              },
        ),
      syncUnlock: (reportId, startedAtMs, durationMs) =>
        set((state) => ({
          startedAt: { ...state.startedAt, [reportId]: startedAtMs },
          durationMs: { ...state.durationMs, [reportId]: durationMs },
        })),
      clearUnlock: (reportId) =>
        set((state) => {
          if (state.startedAt[reportId] === undefined && state.durationMs[reportId] === undefined) {
            return state
          }
          const startedAt = { ...state.startedAt }
          const durationMs = { ...state.durationMs }
          delete startedAt[reportId]
          delete durationMs[reportId]
          return { startedAt, durationMs }
        }),
      clearAll: () => set({ startedAt: {}, durationMs: {} }),
    }),
    {
      name: "report-unlock",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)

/** Clear all in-progress report timers (call on logout). */
export function clearReportUnlocks(): void {
  useReportUnlockStore.getState().clearAll()
  clearIdSet(SHORTENED_STORAGE_KEY)
  clearIdSet(INSTANT_STORAGE_KEY)
}

/**
 * Sync an included-plan report unlock from the subscription start time.
 * `remaining = startedAt + (30|10)min − now`. Liked-review instant unlock skips
 * sync entirely so a reload cannot bring the timer back.
 */
export function syncIncludedReportUnlock(
  reportId: ReportId,
  subscriptionStartedAtMs: number,
): void {
  if (!Number.isFinite(subscriptionStartedAtMs)) return
  if (isIncludedUnlockInstant(reportId)) {
    useReportUnlockStore.getState().clearUnlock(reportId)
    return
  }
  useReportUnlockStore
    .getState()
    .syncUnlock(reportId, subscriptionStartedAtMs, includedUnlockDurationMs(reportId))
}

/** Milliseconds left in a report's unlock window (0 once elapsed / unknown). */
export function unlockRemainingMs(
  startedAt: number | undefined,
  now: number,
  durationMs: number = UNLOCK_DURATION_MS,
): number {
  if (startedAt === undefined) return 0
  return Math.max(0, startedAt + durationMs - now)
}

/**
 * After a liked review: for each active included-report timer, unlock immediately
 * when API-ready; otherwise shorten 30‑min → 10‑min. No-op if `reports` is empty.
 * OTP timers (Love Match etc.) are never touched here.
 */
export function applyIncludedUnlocksAfterLikedReview(
  reports: readonly UserReport[],
): void {
  if (reports.length === 0) return

  for (const id of INCLUDED_UNLOCK_REPORT_IDS) {
    const { startedAt, syncUnlock, clearUnlock } = useReportUnlockStore.getState()
    const started = startedAt[id]
    if (started === undefined) continue

    const report = reports.find((item) => item.type === id)
    const ready = report?.status === "ready" || Boolean(report?.url)

    if (ready) {
      // Persist instant so webapp re-sync keeps duration 0; clear session timer
      // so the UI unlocks immediately (remaining is 0 when startedAt is missing).
      markIncludedUnlockInstant(id)
      clearUnlock(id)
    } else {
      markIncludedUnlockShortened(id)
      syncUnlock(id, started, UNLOCK_DURATION_MS)
    }
  }
}

/** Format remaining ms as M:SS (e.g. "9:56"). */
export function formatUnlockLabel(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}
